/**
 * 🔄 CMT Invitation Atomic Transactions
 * 
 * Handles complex role transfers and user assignments with atomic transactions
 * to ensure data consistency during conflict resolution.
 */

import { 
  doc, 
  getDoc, 
  writeBatch,
  serverTimestamp,
  collection
} from 'firebase/firestore';
import { db } from '../config/firebase';
import cmtInvitationService from './cmtInvitationService';

/**
 * 🎯 Process invitation with role conflict resolution
 * Uses atomic transactions to ensure data consistency
 */
export async function processInvitationWithResolution(invitationData, resolutionMethod, context) {
  const {
    inviterUserId,
    orgId,
    cmtId,
    roleConflict,
    inviterName,
    cmtName,
    orgName
  } = context;

  const batch = writeBatch(db);
  
  try {
    // Step 1: Create new user account
    const newUser = await cmtInvitationService.createUser(
      invitationData.email,
      invitationData.fullName,
      orgId
    );

    console.log(`✅ Created new user: ${newUser.fullName} (${newUser.uid})`);

    // Step 2: Handle conflict resolution if needed
    if (resolutionMethod && resolutionMethod !== 'cancel' && roleConflict?.currentHolders?.length > 0) {
      await handleCurrentRoleHolder(batch, roleConflict.currentHolders[0], resolutionMethod, cmtId, inviterUserId, orgId);
    }

    // Step 3: Assign new user to CMT with target role
    await cmtInvitationService.assignUserToCMT(
      batch,
      newUser.uid,
      cmtId,
      invitationData.cmtRole,
      inviterUserId,
      orgId
    );

    console.log(`✅ Assigned ${newUser.fullName} to ${cmtName} as ${invitationData.cmtRole}`);

    // Step 4: Create comprehensive audit log
    await createAuditLog(batch, {
      action: resolutionMethod ? 'USER_INVITED_WITH_ROLE_TRANSFER' : 'USER_INVITED_TO_CMT',
      performedBy: inviterUserId,
      targetUserId: newUser.uid,
      orgId: orgId,
      cmtId: cmtId,
      assignedRole: invitationData.cmtRole,
      resolutionMethod: resolutionMethod,
      affectedUsers: roleConflict?.currentHolders || [],
      metadata: {
        inviterName: inviterName,
        inviteeEmail: invitationData.email,
        inviteeName: invitationData.fullName,
        cmtName: cmtName,
        orgName: orgName,
        conflictResolved: !!resolutionMethod && resolutionMethod !== 'cancel'
      }
    });

    // Step 5: Commit all changes atomically
    await batch.commit();
    console.log('✅ All invitation changes committed successfully');

    // Step 6: Send email notifications (after successful commit)
    await sendInvitationNotifications({
      newUser,
      invitationData,
      roleConflict,
      resolutionMethod,
      inviterName,
      cmtName,
      orgName
    });

    return {
      success: true,
      newUser,
      message: resolutionMethod ? 
        `Successfully invited ${invitationData.fullName} and resolved role conflict` :
        `Successfully invited ${invitationData.fullName} to ${cmtName}`
    };

  } catch (error) {
    console.error('❌ Error in processInvitationWithResolution:', error);
    
    // Log the failure
    try {
      const failureBatch = writeBatch(db);
      await createAuditLog(failureBatch, {
        action: 'USER_INVITATION_FAILED',
        performedBy: inviterUserId,
        orgId: orgId,
        cmtId: cmtId,
        targetRole: invitationData.cmtRole,
        error: error.message,
        metadata: {
          inviterName: inviterName,
          inviteeEmail: invitationData.email,
          inviteeName: invitationData.fullName,
          cmtName: cmtName,
          resolutionMethod: resolutionMethod
        }
      });
      await failureBatch.commit();
    } catch (logError) {
      console.error('Failed to log invitation failure:', logError);
    }
    
    throw new Error(`Invitation failed: ${error.message}`);
  }
}

/**
 * 🔄 Handle current role holder based on resolution method
 */
async function handleCurrentRoleHolder(batch, currentHolder, resolutionMethod, cmtId, updatedBy, orgId) {
  try {
          switch (resolutionMethod) {
        case 'transfer_demote_member':
          await cmtInvitationService.updateUserCMTRole(
            batch,
            currentHolder.uid,
            cmtId,
            'Member',
            updatedBy,
            orgId
          );
          console.log(`✅ Demoted ${currentHolder.fullName} to Member`);
          break;

        case 'transfer_demote_observer':
          await cmtInvitationService.updateUserCMTRole(
            batch,
            currentHolder.uid,
            cmtId,
            'Observer',
            updatedBy,
            orgId
          );
          console.log(`✅ Demoted ${currentHolder.fullName} to Observer`);
          break;

        case 'transfer_remove':
          await cmtInvitationService.removeUserFromCMT(
            batch,
            currentHolder.uid,
            cmtId,
            updatedBy,
            orgId
          );
          console.log(`✅ Removed ${currentHolder.fullName} from CMT`);
          break;

        default:
          console.warn(`Unknown resolution method: ${resolutionMethod}`);
          throw new Error(`Unknown resolution method: ${resolutionMethod}`);
      }
  } catch (error) {
    console.error(`❌ Error handling current role holder:`, error);
    throw error;
  }
}

/**
 * 📝 Create comprehensive audit log entry
 */
async function createAuditLog(batch, logData) {
  try {
    const logRef = doc(collection(db, 'platform_audit_logs'));
    
    const auditEntry = {
      id: logRef.id,
      timestamp: serverTimestamp(),
      category: 'USER_MANAGEMENT',
      subcategory: 'CMT_INVITATION',
      severity: logData.error ? 'ERROR' : 'INFO',
      ...logData
    };

    batch.set(logRef, auditEntry);
    
    console.log(`📝 Audit log entry created: ${logData.action}`);
    
  } catch (error) {
    console.error('Error creating audit log:', error);
    // Don't fail the transaction for audit log issues
  }
}

/**
 * 📧 Send all relevant email notifications
 */
async function sendInvitationNotifications({
  newUser,
  invitationData,
  roleConflict,
  resolutionMethod,
  inviterName,
  cmtName,
  orgName
}) {
  try {
    // Send invitation email to new user
    await cmtInvitationService.sendInvitationEmail(
      invitationData.email,
      invitationData,
      inviterName,
      cmtName,
      orgName
    );

    // Send notifications to affected users if role transfer occurred
    if (resolutionMethod && resolutionMethod !== 'cancel' && roleConflict?.currentHolders?.length > 0) {
      const currentHolder = roleConflict.currentHolders[0];
      
      switch (resolutionMethod) {
        case 'transfer_demote_member':
          await cmtInvitationService.sendRoleChangeEmail(
            currentHolder.email,
            currentHolder.fullName,
            cmtName,
            invitationData.cmtRole, // old role
            'Member', // new role
            inviterName
          );
          break;

        case 'transfer_demote_observer':
          await cmtInvitationService.sendRoleChangeEmail(
            currentHolder.email,
            currentHolder.fullName,
            cmtName,
            invitationData.cmtRole, // old role
            'Observer', // new role
            inviterName
          );
          break;

        case 'transfer_remove':
          await cmtInvitationService.sendRemovalEmail(
            currentHolder.email,
            currentHolder.fullName,
            cmtName,
            inviterName
          );
          break;
      }
    }

    console.log('✅ All email notifications sent successfully');

  } catch (error) {
    console.error('❌ Error sending notifications:', error);
    // Don't fail the main process for email issues, just log them
  }
}

/**
 * 🎯 Simple invitation without conflicts
 */
export async function processSimpleInvitation(invitationData, context) {
  const {
    inviterUserId,
    orgId,
    cmtId,
    inviterName,
    cmtName,
    orgName
  } = context;

  const batch = writeBatch(db);
  
  try {
    // Create new user
    const newUser = await cmtInvitationService.createUser(
      invitationData.email,
      invitationData.fullName,
      orgId
    );

    // Assign to CMT
    await cmtInvitationService.assignUserToCMT(
      batch,
      newUser.uid,
      cmtId,
      invitationData.cmtRole,
      inviterUserId,
      orgId
    );

    // Create audit log
    await createAuditLog(batch, {
      action: 'USER_INVITED_TO_CMT',
      performedBy: inviterUserId,
      targetUserId: newUser.uid,
      orgId: orgId,
      cmtId: cmtId,
      assignedRole: invitationData.cmtRole,
      metadata: {
        inviterName: inviterName,
        inviteeEmail: invitationData.email,
        inviteeName: invitationData.fullName,
        cmtName: cmtName,
        orgName: orgName
      }
    });

    // Commit changes
    await batch.commit();

    // Send invitation email
    await cmtInvitationService.sendInvitationEmail(
      invitationData.email,
      invitationData,
      inviterName,
      cmtName,
      orgName
    );

    return {
      success: true,
      newUser,
      message: `Successfully invited ${invitationData.fullName} to ${cmtName}`
    };

  } catch (error) {
    console.error('Error in simple invitation:', error);
    throw new Error(`Invitation failed: ${error.message}`);
  }
}

/**
 * 🔄 Update user role in CMT (for existing users)
 */
export async function updateExistingUserRole(userId, cmtId, newRole, updatedBy, orgId) {
  const batch = writeBatch(db);
  
  try {
    // Get current user data
    const userDoc = await getDoc(doc(db, 'c3_users', userId));
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const currentAssignment = userData.cmtAssignments?.find(
      assignment => assignment.cmtId === cmtId && assignment.isActive
    );

    if (!currentAssignment) {
      throw new Error('User is not assigned to this CMT');
    }

    const oldRole = currentAssignment.cmtRole;

    // Check for role conflicts
    const conflict = await cmtInvitationService.checkRoleConflict(cmtId, newRole, userId, orgId);
    if (conflict.hasConflict) {
      throw new Error(`Role ${newRole} is already assigned to another user`);
    }

    // Update user role
    await cmtInvitationService.updateUserCMTRole(batch, userId, cmtId, newRole, updatedBy, orgId);

    // Create audit log
    await createAuditLog(batch, {
      action: 'USER_ROLE_UPDATED',
      performedBy: updatedBy,
      targetUserId: userId,
      orgId: orgId,
      cmtId: cmtId,
      oldRole: oldRole,
      newRole: newRole,
      metadata: {
        userName: userData.fullName || userData.email
      }
    });

    // Commit changes
    await batch.commit();

    // Send notification email
    await cmtInvitationService.sendRoleChangeEmail(
      userData.email,
      userData.fullName || userData.email,
      'CMT', // Would need CMT name lookup
      oldRole,
      newRole,
      'Administrator' // Would need updater name lookup
    );

    return {
      success: true,
      message: `Successfully updated ${userData.fullName}'s role from ${oldRole} to ${newRole}`
    };

  } catch (error) {
    console.error('Error updating user role:', error);
    throw new Error(`Role update failed: ${error.message}`);
  }
}

const cmtInvitationTransactions = {
  processInvitationWithResolution,
  processSimpleInvitation,
  updateExistingUserRole
};

export default cmtInvitationTransactions;
